import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import bffApiCaller from '../../actions/bffApiCaller';
import { Offer, fetchOffers } from '../../actions/services';
import { Button } from '../../components/common/dynamic-form/base/Button/Button';
import styles from './VerificationPage.module.scss';


export const VerificationPage = () => {
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const history = useHistory();

    const [offers, setOffers] = useState<Offer[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isVerifying, setIsVerifying] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
    const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);

    useEffect(() => {
        const controller = new AbortController();

        const loadOffers = async () => {
            try {
                console.log("[Offers] Fetching offers");
                const response = await fetchOffers(controller.signal);
                setOffers(response.offers);
            } catch (err: any) {
                if (err.name !== 'CanceledError') {
                    console.error("[Offers] Failed to fetch offers:", err);
                    setError("Teklifler alınırken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.");
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        };

        loadOffers();

        return () => {
            console.log("[Offers] Unmounting and aborting fetch.");
            controller.abort();
        };
    }, []);

    const handleOfferClick = (offer: Offer) => {
        console.log(`[Offers] Offer Selected: ${offer.offerId}. Action URL: ${offer.cta.actionValue}`);

        // Save the full offer context for the captcha callback
        setSelectedOffer(offer);

        if (recaptchaRef.current) {
            console.log("[Offers] Executing invisible reCAPTCHA...");
            recaptchaRef.current.execute();
        } else {
            console.error("[Offers] ReCAPTCHA reference is null.");
            setError("Güvenlik sistemi yüklenemedi. Lütfen sayfayı yenileyin.");
        }
    };

    const handleCaptchaSuccess = async (token: string | null) => {
        console.log("[Offers] reCAPTCHA success callback fired.");

        // Make sure we have the token, the user, and the specific offer they clicked
        if (!token || !selectedOffer) {
            console.error("[Offers] Missing token or selectedOffer in callback.");
            return;
        }

        setIsVerifying(true);
        setError(null);

        try {
            console.log(`[Offers] Sending token to BFF for verification. Offer: ${selectedOffer.offerId}`);

            // Added userId and offerId to your verification payload
            await bffApiCaller.post('/api/v1/captcha/verify-captcha', {
                captchaToken: token,
                offerId: selectedOffer.offerId
            });
            console.log("[Offers] BFF verification successful.");

            history.push('/identity-verification', { offerId: selectedOffer.offerId });
        } catch (err) {
            console.error("[Offers] Error during verification or redirection:", err);
            setError("Doğrulama başarısız oldu. Lütfen tekrar deneyin.");
        } finally {
            setIsVerifying(false);
            // Always reset captcha so the user can try again if they hit 'back' or if it failed
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
        }
    };

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loader}></div>
                <p>Teklifler yükleniyor...</p>
            </div>
        );
    }

    if (error && offers.length === 0) {
        return (
            <div className={styles.errorContainer}>
                <p className={styles.error}>{error}</p>
            </div>
        );
    }

    return (
        <div className={styles.offersPageLayout}>
            <div className={styles.offersHeader}>
                <h1>Size Özel Kredi Teklifleri</h1>
                <p>İhtiyacınıza en uygun teklifi seçerek başvurunuzu tamamlayın.</p>
            </div>

            <div className={styles.offersList}>
                {offers.map((offer) => (
                    <div key={offer.offerId} className={styles.offerCard}>
                        <div className={styles.cardHeader}>
                            <div className={styles.lenderLogo}>
                                <img src={offer.lender.logoUrl} alt={offer.lender.lenderName} />
                            </div>
                            <span className={styles.segmentBadge}>{offer.customerSegmentText}</span>
                        </div>

                        <div className={styles.cardContent}>
                            <div className={styles.interestBox}>
                                <span className={styles.interestRate}>{offer.interestDisplayText}</span>
                                <span className={styles.interestLabel}>Faiz Oranı</span>
                            </div>

                            <h2 className={styles.offerTitle}>{offer.offerName}</h2>

                            <div className={styles.amountBox}>
                                <span className={styles.amountLabel}>{offer.amount.displayLabel}</span>
                                <span className={styles.amountValue}>
                                    {Number(offer.amount.totalAmount).toLocaleString('tr-TR')} {offer.amount.currency}
                                </span>
                            </div>

                            {offer.highlights && (
                                <p className={styles.highlightText}>
                                    <span className={styles.highlightTick}>✓</span>
                                    {offer.highlights}
                                </p>
                            )}
                        </div>

                        <div className={styles.cardFooter}>
                            <Button
                                // Pass the whole offer object instead of just the URL string
                                onClick={() => handleOfferClick(offer)}
                                isLoading={isVerifying && selectedOffer?.offerId === offer.offerId}
                                disabled={isVerifying}
                            >
                                {offer.cta.buttonText}
                            </Button>
                            <a 
                                href="#" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    setSelectedPdfUrl('/akbank-basvuru-kosullari.pdf');
                                }}
                                className={styles.conditionsLink}
                            >
                                Başvuru Koşulları
                            </a>
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.captchaOverlay} style={{ display: 'none' }}>
                <ReCAPTCHA
                    ref={recaptchaRef}
                    size="invisible"
                    sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || ""}
                    onChange={handleCaptchaSuccess}
                />
            </div>

            {error && <p className={styles.errorFeedback}>{error}</p>}

            {selectedPdfUrl && (
                <div className={styles.modalOverlay} onClick={() => setSelectedPdfUrl(null)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.modalCloseBtn} onClick={() => setSelectedPdfUrl(null)}>Kapat</button>
                        <iframe 
                            src={selectedPdfUrl} 
                            className={styles.modalIframe} 
                            title="Başvuru Koşulları"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};